import { PERCENT_DECIMALS, RoundingType } from "@silo/types";
import { FixedNumber } from "@ethersproject/bignumber";
import { formatUnits } from "@ethersproject/units";
import * as _Ord from "fp-ts/Ord";
import * as _Eq from "fp-ts/Eq";

export const BigMath = {
  abs(value: bigint) {
    return value < 0n ? -value : value;
  },
  sign(value: bigint) {
    if (value === 0n) return 0n;
    return value < 0n ? -1n : 1n;
  },
  pow(base: bigint, exponent: bigint) {
    return base ** exponent;
  },
  min(value: bigint, ...values: bigint[]) {
    for (const v of values) if (v < value) value = v;
    return value;
  },
  max(value: bigint, ...values: bigint[]) {
    for (const v of values) if (v > value) value = v;
    return value;
  },
  sum(values: bigint[]) {
    return values.reduce((total, value) => total + value, 0n);
  },
  clamp(value: bigint, min: bigint, max: bigint) {
    return this.min(this.max(value, min), max);
  },
};

/**
 * Shifts decimals on an integer. Decimals can be positive or negative.
 *
 * ```
 * shiftDecimal(1_000n, 2) // => 100_000n (i.e. 1_000 x 10^2)
 * shiftDecimal(1_000n, -2) // => 10 (i.e. 1_000 x 10^-2)
 * ```
 *
 * For negative decimals, remaining fractions are rounded to the closest integer.
 *
 * ```
 * shiftDecimal(499_999n, -2) // => 5_000n
 * ```
 */
export const shiftDecimal = (
  value: bigint,
  decimals: number,
  roundingType: RoundingType = "half-up"
) => {
  if (!Number.isInteger(decimals)) {
    throw new Error(
      `Must pass an integer for the decimals argument. Value received: ${decimals}`
    );
  }

  if (decimals === 0) return value;

  const exponent = BigInt(decimals);

  if (decimals > 0) {
    return value * 10n ** exponent;
  }

  const by = 10n ** BigMath.abs(exponent);
  const signFactor = BigMath.sign(value);
  const abs = BigMath.abs(value);

  const toValue = abs / by;
  const mod = abs % by;

  const shouldRoundUp = () => {
    if (mod === 0n) return false;

    switch (roundingType) {
      case "half-up":
      default:
        return mod >= by / 2n;
      case "down":
        return false;
      case "up":
        return true;
    }
  };

  const rounded = shouldRoundUp() ? toValue + 1n : toValue;

  return rounded * signFactor;
};

export const percentToPoints = (percent: bigint): number => {
  return (
    FixedNumber.from(formatUnits(percent, PERCENT_DECIMALS)).toUnsafeFloat() *
    100
  );
};

type FilterConditionally<Source, Condition> = Pick<
  Source,
  { [K in keyof Source]: Source[K] extends Condition ? K : never }[keyof Source]
>;

type FilterByBigIntValues<Source> = FilterConditionally<Source, bigint>;

type BigIntKeys<Source> = keyof FilterByBigIntValues<Source>;

export const aggregateBigIntProps = <T, K extends BigIntKeys<T>>(
  records: T[],
  keys: K[]
): Record<K, T[K]> => {
  const initialValue = keys.reduce(
    (obj, key) => ({ ...obj, [key]: 0n }),
    {} as Record<K, T[K]>
  );

  return records.reduce(
    (totals, record) =>
      keys.reduce((obj, key) => {
        // TS cannot infer that these should be bigint values
        const total = totals[key] as unknown as bigint;
        const value = record[key] as unknown as bigint;

        return { ...obj, [key]: total + value };
      }, {} as Record<K, T[K]>),
    initialValue
  );
};

export const roundToSignificantDigits = (
  value: bigint,
  significantDigits: number
): bigint => {
  if (significantDigits < 1) return value;

  const { length } = `${value}`;

  if (length <= significantDigits) return value;

  const FACTOR = 10n ** BigInt(length - significantDigits);

  let quotient = value / FACTOR;
  const remainder = value % FACTOR;

  quotient += remainder >= FACTOR / 2n ? 1n : 0n;

  return quotient * FACTOR;
};

export const bigintOrd: _Ord.Ord<bigint> = {
  equals: _Eq.eqStrict.equals,
  compare: (x, y) => (x < y ? -1 : x > y ? 1 : 0),
};
